import { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import {
  X, Banknote, CreditCard, CheckCircle2, Loader2, ShieldCheck,
  ArrowLeft, Copy, RefreshCw, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import driverApi from "@/lib/driverApi";

const fmt = (v) => `€${Number(v || 0).toFixed(2)}`;
const POLL_MS = 3000; // poll Stripe every 3 s while the QR is on screen

/**
 * Settlement modal — trip-end payment collection.
 * Steps:
 *   choose  → guest picks Cash or Card
 *   cash    → confirm cash + fiscalize
 *   card_qr → show Stripe QR + poll until paid
 *   receipt → show fiscal receipt (QR + PDF link)
 *
 * When balance is €0 we skip straight to the "Confirm arrival" state.
 */
export default function SettlementModal({ ride, onClose, onComplete }) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("choose");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [cardSession, setCardSession] = useState(null); // {url, session_id, amount_eur}
  const [cardStatus, setCardStatus] = useState("loading"); // loading|waiting|paid|error
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await driverApi.get(`/driver/rides/${ride.id}/balance`);
        if (cancelled) return;
        setBalance(data);
        if (data.balance_due_eur <= 0.001 || data.b2b_invoice) setStep("no_balance");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Učitavanje iznosa nije uspjelo");
        if (!cancelled) onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [ride.id, onClose]);

  const submitComplete = useCallback(async (payload) => {
    setBusy(true);
    try {
      const { data } = await driverApi.post(
        `/driver/rides/${ride.id}/complete`,
        payload,
      );
      setReceipt(data);
      setStep("receipt");
      toast.success("Fiskalizirano · račun spreman");
      onComplete?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vožnju nije moguće završiti.");
    } finally {
      setBusy(false);
    }
  }, [ride.id, onComplete]);

  const collectCash = () => submitComplete({
    payment_method: "cash",
    cash_collected: balance.balance_due_eur,
  });

  const collectPrepaid = () => submitComplete({ payment_method: "prepaid" });

  const startCardQr = async () => {
    setStep("card_qr");
    setCardStatus("loading");
    try {
      const { data } = await driverApi.post(
        `/driver/rides/${ride.id}/balance-checkout`,
      );
      setCardSession(data);
      setCardStatus("waiting");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kartično plaćanje nije moguće pokrenuti.");
      setCardStatus("error");
    }
  };

  // Poll Stripe every 3 s while we're waiting for the guest to pay.
  useEffect(() => {
    if (step !== "card_qr" || cardStatus !== "waiting" || !cardSession) return;
    const tick = async () => {
      try {
        const { data } = await driverApi.get(
          `/driver/rides/${ride.id}/balance-status/${cardSession.session_id}`,
        );
        if (data.payment_status === "paid") {
          setCardStatus("paid");
          clearInterval(pollRef.current);
          // Auto-advance: complete trip immediately.
          await submitComplete({
            payment_method: "card",
            stripe_session_id: cardSession.session_id,
          });
        }
      } catch {
        // silent — next tick will retry
      }
    };
    pollRef.current = setInterval(tick, POLL_MS);
    tick(); // fire once immediately
    return () => clearInterval(pollRef.current);
  }, [step, cardStatus, cardSession, ride.id, submitComplete]);

  return (
    <div
      data-testid="settlement-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
    >
      <div className="w-full sm:max-w-md bg-[#0a0a0a] border border-white/10 rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col">
        <Header ride={ride} onClose={onClose} />
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <LoadingBlock />
          ) : step === "receipt" && receipt ? (
            <ReceiptView receipt={receipt} onClose={onClose} />
          ) : step === "card_qr" && balance ? (
            <CardQrView
              amount={balance.balance_due_eur}
              session={cardSession}
              status={cardStatus}
              onBack={() => { setStep("choose"); setCardStatus("loading"); setCardSession(null); }}
              onRetry={startCardQr}
            />
          ) : step === "no_balance" && balance ? (
            <div className="p-5 space-y-5">
              <BalanceSummary balance={balance} />
              <NoBalanceCard onConfirm={collectPrepaid} busy={busy} b2b={balance.b2b_invoice} partner={balance.b2b_partner} />
            </div>
          ) : (
            <div className="p-5 space-y-5">
              <BalanceSummary balance={balance} />
              {balance.b2b_partner && balance.b2b_billing === "pay_in_vehicle" && (
                <div
                  data-testid="settlement-b2b-collect-banner"
                  className="rounded-xl border-2 border-emerald-500/60 bg-emerald-500/10 p-4"
                >
                  <p className="text-emerald-300 font-bold text-sm leading-snug">
                    NAPLATI OD GOSTA ({fmt(balance.balance_due_eur)}) — Gotovina ili SoftPOS
                  </p>
                  <p className="text-xs text-emerald-200/70 mt-1">
                    B2B · {balance.b2b_partner} — vožnja nije na mjesečni račun, naplata na licu mjesta.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 gap-3" data-testid="settlement-choices">
                <PaymentChoiceButton
                  testId="settle-cash-btn"
                  icon={<Banknote className="w-7 h-7 text-emerald-300" />}
                  tint="emerald"
                  eyebrow="Naplati gotovinu + fiskaliziraj"
                  amount={balance.balance_due_eur}
                  caption="Gost plaća u EUR · Fira izdaje fiskalni račun"
                  onClick={collectCash}
                  busy={busy}
                />
                <PaymentChoiceButton
                  testId="settle-card-btn"
                  icon={<CreditCard className="w-7 h-7 text-[#C5A059]" />}
                  tint="gold"
                  eyebrow="Kartica putem QR koda"
                  amount={balance.balance_due_eur}
                  caption="Pokažite QR — gost skenira i plaća na svom mobitelu"
                  onClick={startCardQr}
                  busy={busy}
                />
              </div>
              <p className="text-[10px] text-zinc-600 leading-relaxed border-t border-white/5 pt-3">
                Svaka naplata fiskalizira se u Poreznoj upravi putem Fire.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- sub-components ----
function Header({ ride, onClose }) {
  return (
    <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C5A059]">
          Naplata vožnje · {ride.ref}
        </p>
        <p className="text-sm text-zinc-100 mt-0.5 truncate">
          {ride.name} · {ride.pickup_location?.split(",")[0]}
        </p>
      </div>
      <button
        data-testid="settlement-close"
        onClick={onClose}
        className="w-10 h-10 rounded-xl hover:bg-white/5 flex items-center justify-center text-zinc-400 flex-shrink-0"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="p-12 flex flex-col items-center gap-3 text-zinc-500">
      <Loader2 className="w-6 h-6 animate-spin" />
      <p className="text-xs uppercase tracking-widest">Učitavanje iznosa…</p>
    </div>
  );
}

function BalanceSummary({ balance }) {
  const b2bInvoice = !!balance.b2b_invoice;
  return (
    <div className="rounded-2xl bg-black/40 border border-white/5 p-4 space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-zinc-500">Ukupna cijena</span>
        <span className="text-zinc-200 font-medium">{fmt(balance.quote_amount_eur)}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-zinc-500">{b2bInvoice ? "Na mjesečni B2B račun" : "Polog plaćen online"}</span>
        <span className="text-zinc-400">− {fmt(b2bInvoice ? balance.quote_amount_eur : balance.deposit_paid_eur)}</span>
      </div>
      <div className="pt-2 border-t border-white/5 flex items-center justify-between">
        <span
          data-testid="settlement-balance-label"
          className="text-[10px] uppercase tracking-[0.24em] text-[#C5A059]"
        >
          {b2bInvoice ? "Za naplatu od gosta" : "Za naplatu"}
        </span>
        <span
          data-testid="settlement-balance-amount"
          className="text-2xl font-display text-white"
        >
          {fmt(b2bInvoice ? 0 : balance.balance_due_eur)}
        </span>
      </div>
    </div>
  );
}

function PaymentChoiceButton({ testId, icon, tint, eyebrow, amount, caption, onClick, busy }) {
  const styles = tint === "gold"
    ? "from-[#C5A059]/15 via-[#C5A059]/5 to-transparent border-[#C5A059]/30 hover:border-[#C5A059]/50 [&_.icon-bg]:bg-[#C5A059]/15 [&_.eyebrow]:text-[#C5A059]/80"
    : "from-emerald-500/20 via-emerald-500/10 to-transparent border-emerald-500/40 hover:border-emerald-400/60 [&_.icon-bg]:bg-emerald-500/20 [&_.eyebrow]:text-emerald-300/80";
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={busy}
      className={`group relative h-24 rounded-2xl bg-gradient-to-br ${styles} border text-left px-5 disabled:opacity-60 transition-all overflow-hidden`}
    >
      <div className="flex items-center gap-4">
        <div className="icon-bg w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="eyebrow text-[10px] uppercase tracking-[0.2em]">{eyebrow}</p>
          <p className="text-2xl font-display text-white mt-1">{fmt(amount)}</p>
          <p className="text-[11px] text-zinc-500 mt-0.5 truncate">{caption}</p>
        </div>
      </div>
      {busy && (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-zinc-200" />
        </div>
      )}
    </button>
  );
}

function CardQrView({ amount, session, status, onBack, onRetry }) {
  const copyUrl = async () => {
    if (!session?.url) return;
    try {
      await navigator.clipboard.writeText(session.url);
      toast.success("Poveznica za plaćanje kopirana");
    } catch {
      toast.error("Kopiranje nije uspjelo — dugo pritisnite poveznicu za dijeljenje");
    }
  };
  return (
    <div className="p-5 space-y-4" data-testid="settlement-card-qr">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
        data-testid="card-qr-back"
      >
        <ArrowLeft className="w-3 h-3" />
        Natrag
      </button>

      <div className="rounded-2xl bg-[#C5A059]/5 border border-[#C5A059]/30 p-5 text-center">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C5A059]/90">
          Kartično plaćanje · Gost skenira QR
        </p>
        <p className="text-3xl font-display text-white mt-2">{fmt(amount)}</p>

        {status === "loading" && (
          <div className="mt-6 flex flex-col items-center gap-2 text-zinc-500 py-8">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p className="text-xs uppercase tracking-widest">Priprema plaćanja…</p>
          </div>
        )}

        {status === "error" && (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-red-300">Sesiju plaćanja nije moguće kreirati.</p>
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-2 h-10 px-5 rounded-xl border border-white/10 text-xs uppercase tracking-widest text-zinc-300 hover:text-white"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Pokušaj ponovno
            </button>
          </div>
        )}

        {session && (status === "waiting" || status === "paid") && (
          <>
            <div className="mt-5 mx-auto w-52 h-52 bg-white p-3 rounded-2xl flex items-center justify-center">
              <span
                data-testid="settlement-card-qr-code"
                className="block w-full h-full"
              >
                <QRCode
                  value={session.url}
                  size={192}
                  fgColor="#000"
                  bgColor="#fff"
                  level="M"
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                />
              </span>
            </div>
            <p className="text-xs text-zinc-400 mt-4 leading-relaxed">
              Zamolite gosta da otvori kameru na mobitelu i usmjeri je prema QR kodu.
              Platit će karticom, Apple&nbsp;Pay ili Google&nbsp;Pay na svom mobitelu.
            </p>

            <div className="mt-4 flex items-center justify-center gap-2">
              {status === "waiting" ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#C5A059]" />
                  <span
                    data-testid="card-qr-status"
                    className="text-[10px] uppercase tracking-[0.28em] text-[#C5A059]"
                  >
                    Čekanje uplate…
                  </span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span
                    data-testid="card-qr-status"
                    className="text-[10px] uppercase tracking-[0.28em] text-emerald-400"
                  >
                    Uplata zaprimljena · fiskaliziranje…
                  </span>
                </>
              )}
            </div>

            <div className="mt-5 flex items-center gap-2">
              <button
                data-testid="card-qr-copy-link"
                onClick={copyUrl}
                className="flex-1 h-10 rounded-xl border border-white/10 hover:border-[#C5A059]/40 hover:text-[#C5A059] text-zinc-400 text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 transition-colors"
              >
                <Copy className="w-3 h-3" />
                Kopiraj poveznicu
              </button>
              <a
                href={session.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 h-10 rounded-xl border border-white/10 hover:border-[#C5A059]/40 hover:text-[#C5A059] text-zinc-400 text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Otvori poveznicu
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function NoBalanceCard({ onConfirm, busy, b2b, partner }) {
  return (
    <div className="rounded-2xl bg-emerald-500/5 border border-emerald-500/30 p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-6 h-6 text-emerald-300" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.24em] text-emerald-300/90">
            {b2b ? "NE NAPLAĆUJ OD GOSTA (€0.00) — Mjesečni B2B Račun" : "U cijelosti plaćeno unaprijed"}
          </p>
          <p className="text-sm text-zinc-200 mt-1 leading-relaxed">
            {b2b
              ? `Vožnja se obračunava direktno tvrtki${partner ? ` (${partner})` : ""} na kraju mjeseca — bez gotovine ili kartice pri preuzimanju.`
              : "Gost je već platio cijeli iznos online. Pri preuzimanju se ništa ne naplaćuje."}
          </p>
        </div>
      </div>
      <button
        data-testid="settle-confirm-arrival-btn"
        onClick={onConfirm}
        disabled={busy}
        className="w-full h-14 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black font-semibold text-base flex items-center justify-center gap-2 transition-colors"
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
        {busy ? "Fiskaliziranje…" : "Potvrdi dolazak i zatvori vožnju"}
      </button>
    </div>
  );
}

function ReceiptView({ receipt, onClose }) {
  const inv = receipt.invoice || {};
  const method = (receipt.settlement_method || inv.payment_method || "cash").toUpperCase();
  const isLive = inv.mode === "live";
  const copyRef = async () => {
    if (!inv.invoice_ref) return;
    try {
      await navigator.clipboard.writeText(inv.invoice_ref);
      toast.success("Broj računa kopiran");
    } catch {
      toast.error("Kopiranje nije uspjelo");
    }
  };
  return (
    <div className="p-5 space-y-4" data-testid="settlement-receipt">
      <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-5 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-3">
          <CheckCircle2 className="w-7 h-7 text-emerald-300" />
        </div>
        <p className="text-[10px] uppercase tracking-[0.28em] text-emerald-300/90">
          Fiskalizirano · {isLive ? "Fira · Porezna potvrda" : "Testni način"}
        </p>
        <p className="text-3xl font-display text-white mt-2">
          {fmt(inv.amount_eur ?? receipt.balance_due_eur)}
        </p>
        <p className="text-xs text-zinc-500 mt-1">
          Plaćeno: <span className="text-zinc-200">{method === "CASH" ? "GOTOVINA" : method === "CARD" ? "KARTICA" : method}</span>
        </p>
      </div>

      {/* Prominent invoice number for the driver to read to the guest */}
      {inv.invoice_ref && (
        <div className="rounded-2xl border border-[#C5A059]/30 bg-[#C5A059]/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#C5A059]/90">
                Broj računa
              </p>
              <p
                data-testid="settlement-invoice-ref"
                className="text-2xl font-display font-mono text-white mt-1 tracking-wide"
              >
                {inv.invoice_ref}
              </p>
              {inv.invoice_type && (
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                  {inv.invoice_type}{inv.invoice_id ? ` · ID ${inv.invoice_id}` : ""}
                </p>
              )}
            </div>
            <button
              onClick={copyRef}
              data-testid="settlement-copy-invoice-ref"
              className="p-3 rounded-xl border border-white/10 hover:border-[#C5A059]/40 text-zinc-300 hover:text-[#C5A059] transition-colors"
              aria-label="Copy invoice number"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
            Pročitajte gostu — ovo je njegov dokaz fiskaliziranog računa iz Fire.
          </p>
        </div>
      )}

      {inv.qr_code_url && (
        <div className="flex flex-col items-center gap-2 py-2">
          <img
            data-testid="settlement-qr"
            src={inv.qr_code_url}
            alt="Fiscal receipt QR"
            className="w-44 h-44 rounded-lg bg-white p-2"
          />
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">
            Pokažite gostu za provjeru
          </p>
        </div>
      )}

      <div className="space-y-1.5 text-xs">
        <div className="flex justify-between">
          <span className="text-zinc-500">Fiskalizirano</span>
          <span className="text-zinc-300">{formatWhen(inv.fiscalized_at)}</span>
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        {inv.invoice_id && (
          <a
            data-testid="settlement-view-pdf"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/public/invoice/${inv.invoice_id}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 h-12 rounded-xl border border-white/10 hover:border-[#C5A059]/40 hover:text-[#C5A059] text-zinc-400 text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-colors"
          >
            Pregledaj PDF
          </a>
        )}
        <button
          data-testid="settlement-done-btn"
          onClick={onClose}
          className="flex-1 h-12 rounded-xl bg-[#C5A059] hover:bg-[#D4AF37] text-black text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Natrag na vožnje
        </button>
      </div>
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("hr-HR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}
